import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import Heading from 'app/components/shared/Heading';
import ProgressBar from 'app/components/shared/ProgressBar';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import ClearFacets from 'app/components/shared/Facets/ClearFacets';
import Tabs from 'app/components/shared/Tabs';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import {
  selectActiveFilters,
  selectLocationIdToIsPreferred,
  selectOrderedLocations
} from 'app/selectors/locations/LocationsSearchSelector';
import LocationsSearchForm from './LocationsSearchForm';
import LocationItem from './LocationItem';
import LocationsActiveFilters from './LocationsActiveFilters';
import LocationsMap from './LocationsSearchForm/LocationsMap';

import './LocationsPage.scss';

const TAB_NAMES = {
  GRID: 'grid',
  MAP: 'map'
};

export default function LocationsPage() {
  const intl = useIntl();
  const loc = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(loc.search);
  const query = searchParams.get('address');
  const currentLibrary = useSelector(selectCurrentLibrary);
  const activeFilters = useSelector(selectActiveFilters);
  const isFetching = useSelector(state => state.getIn(['locations', 'locationsSearch', 'isFetching'], false));
  const locationIdToIsPreferred = useSelector(selectLocationIdToIsPreferred);
  let locations = useSelector(selectOrderedLocations);

  if (query) {
    const regex = new RegExp(query, 'i');
    const filteredLocations = locations.filter(l => regex.test(l.get('name')));
    if (filteredLocations.size !== 0) {
      locations = filteredLocations;
      searchParams.delete('address');
      searchParams.set('name', query);
      history.replace(`${loc.pathname}?${searchParams?.toString()}`);
    }
  }

  function renderLocations() {
    if (locations.size > 0) {
      return (
        <ul className="locations-grid">
          {locations.map(location => (
            <li key={location.get('id')}>
              <LocationItem location={location} preferred={locationIdToIsPreferred.get(location.get('id'), false)} />
            </li>
          ))}
        </ul>
      );
    }

    return (
      <div className="empty-locations">
        <Heading tagName="h2" size="medium">
          <FormattedMessage id="empty_locations_search_heading" />
        </Heading>
        <FormattedMessage id="empty_locations_search_description" tagName="p" />
        <ClearFacets>
          <FormattedMessage id="explore_all_locations" />
        </ClearFacets>
      </div>
    );
  }

  function renderLocationCount() {
    return locations.size ? (
      <div className="locations-count">
        <FormattedMessage id="locations_count" values={{ count: locations.size }} />
      </div>
    ) : null;
  }

  const tabOptions = [
    {
      id: TAB_NAMES.GRID,
      label: intl.formatMessage({ id: `locations_tab_${TAB_NAMES.GRID}` }),
      renderPanelBody: () => (
        <>
          {renderLocationCount()}
          {renderLocations()}
        </>
      )
    },
    {
      id: TAB_NAMES.MAP,
      label: intl.formatMessage({ id: `locations_tab_${TAB_NAMES.MAP}` }),
      renderPanelBody: () => (
        <>
          {renderLocationCount()}
          <LocationsMap />
        </>
      )
    }
  ];

  return (
    <>
      <DocumentHeadContainer
        currentLibrary={currentLibrary}
        pageTitle={intl.formatMessage({ id: 'locations' })}
        description={intl.formatMessage(
          { id: 'locations_page_description' },
          { libraryName: currentLibrary.get('fullName') }
        )}
      />
      <ProgressBar active={isFetching} />
      <div className="cp-locations-page">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <Heading className="page-heading" tagName="h1" size="large" variant>
                <FormattedMessage id="locations" />
              </Heading>
              <LocationsSearchForm activeFilters={activeFilters} />
              <LocationsActiveFilters activeFilters={activeFilters} />
              <Tabs ariaLabel={intl.formatMessage({ id: 'locations' })} centered options={tabOptions} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
